/*
 * bd-sample-order.js
 *
 * One copy of the sample-CTA behaviour, shared by every snippet that renders a
 * .bd-sample-order-wrap:
 *
 *   snippets/sample-order-button.liquid   PDP block + product cards
 *   snippets/bd-sample-banner.liquid      floating button over the PDP gallery
 *
 * Both used to carry their own near-identical inline copy behind a shared
 * window.__bdSampleBtnInit guard, which meant only one of them ever ran and
 * which one depended on DOM order. They had drifted apart — different fallback
 * phone numbers, different button-class attributes — so the behaviour of a
 * given page depended on which copy happened to win. Order BD_126496 shipped a
 * "(Please Call)" sample because of a gap in that logic, so it now lives in one
 * file where a fix lands once.
 *
 * The wrap carries everything this needs:
 *   data-product-id             for the PDP variant -> samples_offered map
 *   data-product-handle         to look up the sample variant by SKU
 *   data-sample-suffix          "-S" free sample (default)
 *   data-call-number            "Please Call" target
 *   data-btn-class              full class list for a button this creates
 *   data-button-style           legacy alias for the above (bd-sample-banner)
 *   data-initial-samples-offered
 *                               cards only: the status of the variant currently
 *                               shown. Republished by assets/product-card.js on
 *                               every swatch change.
 *
 * ENFORCEMENT NOTE: everything here is presentation. A shopper can reach
 * /cart/add.js directly, follow a cart permalink, or return to a cart built
 * before a SKU was flagged, and none of that passes through this file. The
 * binding gate is snippets/bd-sample-gate.liquid, at the checkout boundary.
 */
(function () {
  if (window.__bdSampleOrderInit) return;
  window.__bdSampleOrderInit = true;
  // Retire the old per-snippet guard so a cached inline copy stays inert.
  window.__bdSampleBtnInit = true;

  var DEFAULT_CALL_NUMBER = '1-877-631-2845';
  var DEFAULT_BTN_CLASS = 'btn btn--secondary bd-btn--medium w-full block js-sample-order';

  function wrapFor(el) {
    return el.closest('.bd-sample-order-wrap');
  }

  function btnClassFor(wrap) {
    if (wrap.dataset.btnClass) return wrap.dataset.btnClass;
    // bd-sample-banner publishes only the style half and appends the rest.
    if (wrap.dataset.buttonStyle) return wrap.dataset.buttonStyle + ' w-full block js-sample-order';
    return DEFAULT_BTN_CLASS;
  }

  /*
   * The PDP renders its sample CTA outside the add-to-cart form, so it falls back
   * to a document-wide lookup for the selected variant. A card must NOT do that:
   * on a collection page the first [name="id"] in the document belongs to some
   * other tile, and on a page that also shows a product it belongs to that
   * product — either way the card would resolve a variant from somewhere else
   * entirely and then add *that* floor's sample.
   *
   * The card marks itself with data-sample-scope rather than being sniffed out by
   * ancestor class, because "is this a card" has three different answers in this
   * theme (product-card, .card--product, .bdc-card) and the PDP has generic .card
   * containers of its own — the sticky add-to-cart bar, related-product rows.
   * Guessing wrong in one direction strands the PDP button with no variant; in
   * the other it hands a card the wrong one.
   */
  function getVariantIdFromForm(startEl) {
    try {
      var scoped = startEl.closest('form[action*="/cart/add"]') || startEl.closest('product-form');
      if (scoped) {
        var scopedInput = scoped.querySelector('[name="id"]');
        return scopedInput ? String(scopedInput.value) : null;
      }

      var wrap = startEl.closest('.bd-sample-order-wrap');
      if (wrap && wrap.dataset.sampleScope === 'card') return null;

      var input = document.querySelector('[name="id"]');
      return input ? String(input.value) : null;
    } catch (e) {
      return null;
    }
  }

  /*
   * Precedence matters here. data-initial-samples-offered is republished on every
   * swatch change, so it is fresher than anything we could infer from the button
   * we are about to relabel. Reading the button first was the BD_126496 bug: a
   * card that opened on an "Available" colourway kept saying "Add a Free Sample"
   * after the shopper switched to a "Please Call" one, and the click handler
   * trusted that stale label.
   */
  function getSamplesOffered(productId, variantId, wrap) {
    var mapEl = document.getElementById('samples-offered-' + productId);
    if (mapEl) {
      try {
        var map = JSON.parse(mapEl.textContent || '{}');
        if (Object.prototype.hasOwnProperty.call(map, variantId)) {
          return map[variantId] || 'Available';
        }
      } catch (e) {}
    }

    if (wrap && wrap.dataset.initialSamplesOffered) {
      return wrap.dataset.initialSamplesOffered;
    }

    if (wrap) {
      var btn = wrap.querySelector('.js-sample-order');
      if (btn) {
        if (btn.dataset.action === 'call') return 'Please Call';
        if (btn.textContent && btn.textContent.toLowerCase().indexOf('paid') > -1) return 'Paid Sample';
        if (btn.textContent && btn.textContent.toLowerCase().indexOf('free') > -1) return 'Available';
      }
    }

    return 'Available';
  }

  function labelFor(status, wrap) {
    var v = (status || '').trim().toLowerCase();
    if (v === 'please call') return wrap.dataset.labelCall || 'Call for sample';
    if (v === 'paid sample') return wrap.dataset.labelPaid || 'Order paid sample';
    return wrap.dataset.labelFree || 'Add a free sample';
  }

  function actionFor(status) {
    var v = (status || '').trim().toLowerCase();
    if (v === 'please call') return 'call';
    if (v === 'not available') return 'none';
    return 'add-sample';
  }

  // The promise only holds for a free sample, so it follows the status too.
  function syncPromise(wrap, status) {
    var promise = wrap.querySelector('.bd-sample-promise');
    if (!promise) return;
    var v = (status || '').trim().toLowerCase();
    promise.hidden = (v === 'please call' || v === 'paid sample' || v === 'not available');
  }

  function ensureButtonForStatus(wrap, status, variantId) {
    var v = (status || '').trim().toLowerCase();
    var btn = wrap.querySelector('.js-sample-order');

    syncPromise(wrap, status);

    if (v === 'not available') {
      if (btn) btn.remove();
      return;
    }

    var wanted = btnClassFor(wrap);

    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = wanted;
      // Prepend, not append: a wrap may also hold the promise line, which
      // belongs under the button it pre-frames.
      wrap.insertBefore(btn, wrap.firstChild);
    } else if (btn.className !== wanted) {
      btn.className = wanted;
    }

    btn.textContent = labelFor(status, wrap);
    if (variantId) btn.dataset.variantId = String(variantId);
    btn.dataset.action = actionFor(status);
  }

  function updateWrap(wrap) {
    var productId = wrap.dataset.productId;
    var btn = wrap.querySelector('.js-sample-order');

    // Don't bail when the button is missing — it is removed for "Not Available"
    // variants and has to come back when the shopper switches to one that does
    // offer a sample.
    var variantId = getVariantIdFromForm(wrap) || (btn ? btn.dataset.variantId : null);
    if (!variantId) return;

    ensureButtonForStatus(wrap, getSamplesOffered(productId, String(variantId), wrap), variantId);
  }

  function updateAll(root) {
    (root || document).querySelectorAll('.bd-sample-order-wrap').forEach(updateWrap);
  }

  updateAll();

  document.addEventListener('change', function (e) {
    if (!e.target.matches('[name="id"]')) return;
    updateAll(e.target.closest('.product, product-form, product-card, .card--product') || document);
  });

  ['variant:change', 'theme:variant:change'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var container = e.target.closest ? e.target.closest('.product, product-form, product-card, .card--product') : null;
      updateAll(container || document);
    });
  });

  /*
   * Product cards switch colourway without any of the events above — swatches are
   * radios scoped to the card, not the [name="id"] input a form would carry. So
   * assets/product-card.js republishes the new status onto the wrap and fires
   * this, and the label and action follow.
   */
  document.addEventListener('bd:sample:variant-change', function (e) {
    var wrap = e.target && e.target.closest ? e.target.closest('.bd-sample-order-wrap') : null;
    if (wrap) updateWrap(wrap);
    else updateAll();
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.js-sample-order');
    if (!btn) return;

    var wrap = wrapFor(btn);
    if (!wrap) return;

    var handle = wrap.dataset.productHandle;
    var suffix = wrap.dataset.sampleSuffix || '-S';
    var callNumber = wrap.dataset.callNumber || DEFAULT_CALL_NUMBER;
    var productId = wrap.dataset.productId;

    var variantId = btn.dataset.variantId || getVariantIdFromForm(btn);
    if (!variantId) {
      alert('Could not find selected variant.');
      return;
    }

    /*
     * Always re-resolve. The old code only re-checked when the PDP JSON map was
     * present and otherwise trusted btn.dataset.action, which is precisely the
     * state that goes stale on a card.
     */
    var status = getSamplesOffered(productId, String(variantId), wrap);
    var action = actionFor(status);

    if (action === 'none') {
      e.preventDefault();
      return;
    }

    if (action === 'call') {
      e.preventDefault();
      // Relabel before leaving, so a stale button can never invite a second click.
      ensureButtonForStatus(wrap, status, variantId);
      window.location.href = 'tel:' + callNumber.replace(/[^\d+]/g, '');
      return;
    }

    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    var priorError = wrap.querySelector('[data-bd-sample-error]');
    if (priorError) priorError.remove();
    fetch('/products/' + handle + '.js')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var variants = data.variants || [];
        var base = variants.find(function (v) { return String(v.id) === String(variantId); });
        if (!base) { alert('Could not find selected variant.'); return; }

        var sampleSku = (base.sku || '').trim() + suffix;
        var sample = variants.find(function (v) { return v.sku === sampleSku; });
        if (!sample) { alert('No sample found for SKU: ' + sampleSku); return; }

        return window.BDSampleQuantity.add([{ id: sample.id, sku: sample.sku, quantity: 1 }]);
      })
      .then(async function (ok) {
        if (!ok) return;
        if (!ok.ok) {
          var error = await ok.json();
          throw new Error(error.description || error.message || 'Failed to add sample to cart.');
        }
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
      })
      .catch(function (error) {
        var notice = document.createElement('p');
        notice.className = 'alert mt-4 text-sm';
        notice.setAttribute('role', 'alert');
        notice.setAttribute('data-bd-sample-error', '');
        notice.textContent = error.message || 'Failed to add sample to cart.';
        wrap.appendChild(notice);
      })
      .finally(function () { btn.disabled = false; });
  });
})();

